export type NavigationPlan = 'free' | 'basic' | 'full' | 'ai' | 'friends'
export type NavigationRole = 'user' | 'member' | 'admin'
export type NavigationLayout = 'app' | 'standalone'
export type NavigationIcon =
  | 'HomeOutlined'
  | 'BellOutlined'
  | 'CheckSquareOutlined'
  | 'CalendarOutlined'
  | 'DollarCircleFilled'
  | 'WalletOutlined'
  | 'ReloadOutlined'
  | 'LineChartOutlined'
  | 'BulbOutlined'
  | 'SettingOutlined'
  | 'GiftOutlined'
  | 'RiseOutlined'
  | 'ClockCircleOutlined'
  | 'TagOutlined'
  | 'TeamOutlined'
  | 'UserAddOutlined'
  | 'HistoryOutlined'
  | 'DeleteOutlined'
  | 'WarningOutlined'
  | 'LogoutOutlined'
  | 'ShoppingOutlined'
  | 'SafetyCertificateOutlined'
  | 'QuestionCircleOutlined'

export type NavigationNodeType = 'item' | 'group' | 'divider' | 'action'

export interface NavigationBottomMeta {
  order: number
  to: string
  matches?: string[]
}

export interface NavigationNode {
  key: string
  path?: string
  icon?: NavigationIcon
  translationKey?: string
  menuGroup?: 'primary' | 'secondary'
  type?: NavigationNodeType
  allowedPlans?: NavigationPlan[]
  allowedRoles?: NavigationRole[]
  layout?: NavigationLayout
  bottomNav?: NavigationBottomMeta
  hidden?: boolean
  children?: NavigationNode[]
}

export const ALWAYS_VISIBLE_PATHS: string[] = ['/subscribe']
export const ALWAYS_VISIBLE_SEGMENTS: string[] = ['gpt-analysis']

const DEFAULT_ALLOWED_ROLES: NavigationRole[] = ['user', 'member', 'admin']

export const navigationConfig: NavigationNode[] = [
  {
    key: 'home',
    path: '/',
    icon: 'HomeOutlined',
    translationKey: 'menu.home',
    menuGroup: 'primary',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    layout: 'app',
    bottomNav: {
      order: 0,
      to: '/',
      matches: ['/', '/deadline-list', 'deadlines'],
    },
  },
  {
    key: 'notifications',
    path: '/notifications',
    icon: 'BellOutlined',
    translationKey: 'menu.notifications',
    menuGroup: 'primary',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    layout: 'app',
    bottomNav: {
      order: 5,
      to: '/notifications',
      matches: ['/notifications'],
    },
  },
  {
    key: 'tasks-to-confirm',
    path: '/tasks-to-confirm',
    icon: 'CheckSquareOutlined',
    translationKey: 'menu.tasksToConfirm',
    menuGroup: 'primary',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    layout: 'app',
  },
  {
    key: 'task-events',
    path: '/task-events',
    icon: 'HistoryOutlined',
    translationKey: 'menu.taskEvents',
    menuGroup: 'primary',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    hidden: true,
    layout: 'app',
  },
  // {
  //   key: 'deadlines',
  //   icon: 'CalendarOutlined',
  //   translationKey: 'menu.deadlines',
  //   type: 'group',
  //   allowedRoles: DEFAULT_ALLOWED_ROLES,
  //   children: [
  //     {
  //       key: 'deadline-list',
  //       path: '/deadline-list',
  //       icon: 'CalendarOutlined',
  //       translationKey: 'menu.manageDeadlines',
  //       allowedRoles: DEFAULT_ALLOWED_ROLES,
  //       layout: 'app',
  //     },
  //   ],
  // },
  {
    key: 'cashflow',
    icon: 'DollarCircleFilled',
    translationKey: 'menu.cashFlow',
    type: 'group',
    menuGroup: 'primary',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    bottomNav: {
      order: 1,
      to: '/transactions-hub',
      matches: [
        'cashflow',
        '/transactions-hub',
        '/finance-analysis',
        '/expenses-list',
        '/income-list',
        '/recurrent-hub',
        '/recurrent-expense-list',
        '/recurrent-income-list',
      ],
    },
    children: [
      {
        key: 'transactions-hub',
        path: '/transactions-hub',
        icon: 'HistoryOutlined',
        translationKey: 'menu.transactionsHub',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'finance-analysis',
        path: '/finance-analysis',
        icon: 'LineChartOutlined',
        translationKey: 'menu.financeAnalysis',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'expenses-list',
        path: '/expenses-list',
        icon: 'ShoppingOutlined',
        translationKey: 'menu.expenses',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'income-list',
        path: '/income-list',
        icon: 'DollarCircleFilled',
        translationKey: 'menu.income',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'balance-history',
        path: '/balance-history',
        icon: 'LineChartOutlined',
        translationKey: 'menu.history',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
    ],
  },
  {
    key: 'accounts',
    icon: 'WalletOutlined',
    translationKey: 'menu.accounts',
    type: 'group',
    menuGroup: 'primary',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    bottomNav: {
      order: 2,
      to: '/account-list',
      matches: ['accounts', '/account-list', '/obligations', '/balance-history', '/closing-history', '/debt-list'],
    },
    children: [
      {
        key: 'account-list',
        path: '/account-list',
        icon: 'WalletOutlined',
        translationKey: 'menu.manage',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'obligations',
        path: '/obligations',
        icon: 'SafetyCertificateOutlined',
        translationKey: 'menu.obligations',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'forecast-summary-calendar',
        path: '/forecast-summary-calendar',
        icon: 'CalendarOutlined',
        translationKey: 'menu.summaryCalendar',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'debt-list',
        path: '/debt-list',
        icon: 'WarningOutlined',
        translationKey: 'menu.debts',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'closing-history',
        path: '/closing-history',
        icon: 'CalendarOutlined',
        translationKey: 'menu.closingHistory',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
    ],
  },
  {
    key: 'recurring',
    icon: 'ReloadOutlined',
    translationKey: 'menu.recurring',
    type: 'group',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    children: [
      {
        key: 'recurrent-hub',
        path: '/recurrent-hub',
        icon: 'ReloadOutlined',
        translationKey: 'menu.recurrentHub',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'recurrent-expense-list',
        path: '/recurrent-expense-list',
        icon: 'ReloadOutlined',
        translationKey: 'menu.expenses',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'recurrent-income-list',
        path: '/recurrent-income-list',
        icon: 'ReloadOutlined',
        translationKey: 'menu.income',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
    ],
  },
  {
    key: 'budgeting',
    icon: 'WalletOutlined',
    translationKey: 'menu.budgeting',
    type: 'group',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    children: [
      {
        key: 'budget-list',
        path: '/budget-list',
        icon: 'WalletOutlined',
        translationKey: 'menu.budgets',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'expense-group-list',
        path: '/expense-group-list',
        icon: 'ShoppingOutlined',
        translationKey: 'menu.groups',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
    ],
  },
  {
    key: 'tools',
    icon: 'SettingOutlined',
    translationKey: 'menu.toolsHub',
    type: 'group',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    children: [
      {
        key: 'deadline-list',
        path: '/deadline-list',
        icon: 'CalendarOutlined',
        translationKey: 'menu.manageDeadlines',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'tempo-mint',
        path: '/tempo-mint',
        icon: 'ClockCircleOutlined',
        translationKey: 'menu.tempoMint',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'debt-payoff',
        path: '/debt-payoff',
        icon: 'RiseOutlined',
        translationKey: 'menu.debtPayoff',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'finance-goals',
        path: '/finance-goals',
        icon: 'RiseOutlined',
        translationKey: 'menu.financeGoals',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
    ],
  },

  {
    key: 'ai',
    icon: 'SafetyCertificateOutlined',
    translationKey: 'menu.aiTools',
    type: 'group',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    children: [
      {
        key: 'gpt-analysis',
        path: '/gpt-analysis',
        icon: 'LineChartOutlined',
        translationKey: 'menu.gptAnalysis',
        allowedPlans: ['full', 'ai', 'friends'],
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'gpt-analysis-detail',
        path: '/gpt-analysis/:id',
        translationKey: 'menu.gptAnalysis',
        allowedPlans: ['full', 'ai', 'friends'],
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
        hidden: true,
      },
      {
        key: 'forecast-ai-history',
        path: '/settings/forecast-ai-history',
        icon: 'HistoryOutlined',
        translationKey: 'menu.forecastAiHistory',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'finance-analysis-history',
        path: '/settings/finance-analysis-history',
        icon: 'HistoryOutlined',
        translationKey: 'menu.financeAnalysisHistory',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'finance-analysis-history-detail',
        path: '/settings/finance-analysis-history/:id',
        translationKey: 'menu.financeAnalysisHistory',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
        hidden: true,
      },
      {
        key: 'forecast-ai-history-detail',
        path: '/settings/forecast-ai-history/:id',
        translationKey: 'menu.forecastAiHistory',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
        hidden: true,
      },
    ],
  },
  // {
  //   key: 'forecast',
  //   icon: 'CalendarOutlined',
  //   translationKey: 'menu.forecast',
  //   type: 'group',
  //   menuGroup: 'primary',
  //   allowedRoles: DEFAULT_ALLOWED_ROLES,
  //   bottomNav: {
  //     order: 3,
  //     to: '/forecast',
  //     matches: [
  //       'forecast',
  //       '/forecast',
  //       '/forecast-summary',
  //       '/forecast-summary-calendar',
  //       '/tempo-mint',
  //       '/debt-payoff',
  //       '/gpt-analysis',
  //     ],
  //   },
  //   children: [
  //     // {
  //     //   key: 'forecast-view',
  //     //   path: '/forecast',
  //     //   icon: 'CalendarOutlined',
  //     //   translationKey: 'menu.view',
  //     //   allowedRoles: DEFAULT_ALLOWED_ROLES,
  //     //   layout: 'app',
  //     // },
  //     // {
  //     //   key: 'forecast-summary',
  //     //   path: '/forecast-summary',
  //     //   icon: 'RiseOutlined',
  //     //   translationKey: 'menu.summary',
  //     //   allowedRoles: DEFAULT_ALLOWED_ROLES,
  //     //   layout: 'app',
  //     // },
  //     {
  //       key: 'forecast-summary-calendar',
  //       path: '/forecast-summary-calendar',
  //       icon: 'CalendarOutlined',
  //       translationKey: 'menu.summaryCalendar',
  //       allowedRoles: DEFAULT_ALLOWED_ROLES,
  //       layout: 'app',
  //     },
  //     {
  //       key: 'gpt-analysis-detail',
  //       path: '/gpt-analysis/:id',
  //       translationKey: 'menu.gptAnalysis',
  //       allowedPlans: ['full', 'ai'],
  //       allowedRoles: DEFAULT_ALLOWED_ROLES,
  //       layout: 'app',
  //       hidden: true,
  //     },
  //   ],
  // },

  {
    key: 'insights',
    icon: 'BulbOutlined',
    translationKey: 'menu.insights',
    type: 'group',
    menuGroup: 'primary',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    bottomNav: {
      order: 4,
      to: '/my-insights',
      matches: ['insights', '/my-insights', '/public-insights'],
    },
    children: [
      {
        key: 'my-insights',
        path: '/my-insights',
        icon: 'BulbOutlined',
        translationKey: 'menu.myInsights',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'my-insights-detail',
        path: '/my-insights/:insightId',
        translationKey: 'menu.myInsights',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
        hidden: true,
      },
      {
        key: 'public-insights',
        path: '/public-insights',
        icon: 'BulbOutlined',
        translationKey: 'menu.publicInsights',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'public-insights-detail',
        path: '/public-insights/:insightId',
        translationKey: 'menu.publicInsights',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
        hidden: true,
      },
    ],
  },
  {
    key: 'settings',
    icon: 'SettingOutlined',
    translationKey: 'menu.settings',
    type: 'group',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
    children: [
      {
        key: 'subscribe',
        path: '/subscribe',
        icon: 'GiftOutlined',
        translationKey: 'menu.subscribe',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'standalone',
      },
      {
        key: 'settings-billing-history',
        path: '/settings/billing-history',
        icon: 'HistoryOutlined',
        translationKey: 'menu.billingHistory',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'standalone',
      },
      {
        key: 'settings-passkeys',
        path: '/settings/passkeys',
        icon: 'KeyOutlined',
        translationKey: 'menu.passkeys',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'setup-profile',
        path: '/setup-profile',
        icon: 'SettingOutlined',
        translationKey: 'menu.profile',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'standalone',
      },
      {
        key: 'categories',
        path: '/categories',
        icon: 'TagOutlined',
        translationKey: 'menu.categories',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'rewards',
        path: '/rewards',
        icon: 'GiftOutlined',
        translationKey: 'menu.rewards',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      // {
      //   key: 'growth-program',
      //   path: '/growth-program',
      //   icon: 'RiseOutlined',
      //   translationKey: 'menu.growthProgram',
      //   allowedRoles: DEFAULT_ALLOWED_ROLES,
      //   layout: 'app',
      // },
      // {
      //   key: 'skills-factory',
      //   path: '/skills-factory',
      //   icon: 'RiseOutlined',
      //   translationKey: 'menu.skillsFactory',
      //   allowedRoles: DEFAULT_ALLOWED_ROLES,
      //   layout: 'app',
      // },
      // {
      //   key: 'personal-circle',
      //   path: '/personal-circle',
      //   icon: 'TeamOutlined',
      //   translationKey: 'menu.personalCircle',
      //   allowedRoles: DEFAULT_ALLOWED_ROLES,
      //   layout: 'app',
      // },
      {
        key: 'invite-friend',
        path: '/invite-friend',
        icon: 'UserAddOutlined',
        translationKey: 'menu.inviteFriend',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'settings-activity-log',
        path: '/settings/activity-log',
        icon: 'HistoryOutlined',
        translationKey: 'menu.activityLog',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'settings-balance-rollout',
        path: '/settings/balance-rollout',
        icon: 'SafetyCertificateOutlined',
        translationKey: 'menu.balanceRollout',
        allowedRoles: ['admin'],
        layout: 'app',
      },
      {
        key: 'settings-forecast-rollout',
        path: '/settings/forecast-rollout',
        icon: 'SafetyCertificateOutlined',
        translationKey: 'menu.forecastRollout',
        allowedRoles: ['admin'],
        layout: 'app',
      },
      {
        key: 'settings-support-help',
        path: '/settings/support-help',
        icon: 'QuestionCircleOutlined',
        translationKey: 'menu.supportHelp',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
      {
        key: 'settings-delete-account',
        path: '/settings/delete-account',
        icon: 'DeleteOutlined',
        translationKey: 'menu.deleteAccount',
        allowedRoles: DEFAULT_ALLOWED_ROLES,
        layout: 'app',
      },
    ],
  },
  {type: 'divider', key: 'divider-1'},
  {
    key: 'logout',
    icon: 'LogoutOutlined',
    translationKey: 'menu.logout',
    type: 'action',
    allowedRoles: DEFAULT_ALLOWED_ROLES,
  },
]

export function flattenNavigation(nodes: NavigationNode[]): NavigationNode[] {
  const result: NavigationNode[] = []

  const walk = (items: NavigationNode[]) => {
    for (const item of items) {
      result.push(item)
      if (item.children?.length) {
        walk(item.children)
      }
    }
  }

  walk(nodes)
  return result
}
